import Service from "../service";

export const IFSC_REGISTRY_ERROR = "IFSC code not found. Please check and re-enter.";

const IFSC_FORMAT = /^[A-Z]{4}0[A-Z0-9]{6}$/i;

// Confirmed lookups only (valid or not found) so a failed call is retried next time.
const lookupCache = new Map();

/**
 * Look an IFSC up through the server. `error` is set only for a definite "not found";
 * a network failure leaves both fields empty so it never blocks a form.
 * @param {string} value
 * @param {{ candidate?: boolean }} [options] candidate: use the public onboarding route
 * @returns {Promise<{ error: string, details: null | { bank: string, branch: string, city: string, state: string } }>}
 */
export const lookupIfsc = async (value, { candidate = false } = {}) => {
  const code = String(value ?? "").trim().toUpperCase();
  if (!code || !IFSC_FORMAT.test(code)) return { error: "", details: null };
  if (lookupCache.has(code)) return lookupCache.get(code);
  try {
    const response = await Service.makeAPICall({
      methodName: Service.getMethod,
      api_url: `${candidate ? Service.candidateIfscCheck : Service.ifscCheck}/${code}`,
    });
    const result = response?.data;
    if (result?.status === 200) {
      const data = result.data || {};
      const outcome = {
        error: "",
        details: data.unavailable || !data.bank
          ? null
          : { bank: data.bank, branch: data.branch, city: data.city, state: data.state },
      };
      if (!data.unavailable) lookupCache.set(code, outcome);
      return outcome;
    }
    const outcome = { error: result?.message || IFSC_REGISTRY_ERROR, details: null };
    lookupCache.set(code, outcome);
    return outcome;
  } catch (error) {
    return { error: "", details: null };
  }
};

/**
 * Check an IFSC against the server registry lookup. Resolves to an error message,
 * or "" when the code is empty, valid, or the lookup could not be reached.
 * @param {string} value
 * @param {{ candidate?: boolean }} [options]
 * @returns {Promise<string>}
 */
export const getIfscRegistryError = async (value, options) => (await lookupIfsc(value, options)).error;
